/**
 * Stockout Prediction Service
 * TASK-019: Implements stockout prediction per CONTEXT.md Section 2.3
 *
 * Initial Implementation (rule-based, no ML):
 * 1. Calculate average daily sales velocity per product
 * 2. Days until stockout = Current Stock / Daily Sales Average
 * 3. Flag products with days_until_stockout < 7
 */

import { supabase } from './supabase';
import type {
  StockoutEvent,
  StockoutPrediction,
  StockoutRootCause,
  HighRiskStockoutPrediction,
  ActiveStockout,
  AnomalyScore,
  AnomalyEntityType,
  AnomalyCategory,
  AnomalySeverity,
  AnomalyInvestigationStatus,
  DemandForecast,
  InventoryHealthMetrics,
  DateRange,
  ABCClass,
  PredictionRiskLevel,
  ConfidenceLevel,
  PredictionModelType,
  StockoutPredictionResult,
} from '@/types';

// ============================================
// DATABASE ROW TYPES
// ============================================

interface DbStockoutEvent {
  id: string;
  tenant_id: string;
  product_id: string;
  store_id: string;
  stockout_start: string;
  stockout_end: string | null;
  duration_hours: number | null;
  is_resolved: boolean;
  estimated_lost_sales: number | null;
  estimated_lost_units: number | null;
  customer_impact_count: number;
  root_cause: string | null;
  root_cause_notes: string | null;
  linked_po_id: string | null;
  linked_case_id: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface DbStockoutPrediction {
  id: string;
  tenant_id: string;
  product_id: string;
  store_id: string;
  prediction_date: string;
  predicted_stockout_date: string | null;
  days_until_stockout: number | null;
  probability: number;
  current_stock: number;
  daily_sales_avg: number;
  daily_sales_stddev: number | null;
  lead_time_days: number | null;
  pending_po_units: number;
  expected_po_arrival: string | null;
  confidence_level: string;
  confidence_score: number | null;
  model_version: string | null;
  model_type: string;
  risk_level: string;
  is_active: boolean;
  was_accurate: boolean | null;
  actual_stockout_date: string | null;
  alert_generated: boolean;
  alert_id: string | null;
  created_at: string;
}

interface DbAnomalyScore {
  id: string;
  tenant_id: string;
  entity_type: string;
  entity_id: string;
  score_date: string;
  anomaly_score: number;
  anomaly_type: string | null;
  anomaly_category: string | null;
  baseline_value: number | null;
  observed_value: number | null;
  deviation_percentage: number | null;
  z_score: number | null;
  metric_name: string | null;
  model_version: string | null;
  detection_method: string | null;
  severity: string;
  requires_investigation: boolean;
  investigation_status: string;
  investigated_by: string | null;
  linked_alert_id: string | null;
  linked_case_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface DbDemandForecast {
  id: string;
  tenant_id: string;
  product_id: string;
  store_id: string | null;
  forecast_date: string;
  forecast_horizon_days: number;
  predicted_daily_demand: number;
  predicted_weekly_demand: number | null;
  predicted_monthly_demand: number | null;
  demand_lower_bound: number | null;
  demand_upper_bound: number | null;
  confidence_interval: number;
  seasonality_factor: number;
  is_promotional_period: boolean;
  actual_demand: number | null;
  forecast_error: number | null;
  mape: number | null;
  model_version: string | null;
  model_type: string | null;
  created_at: string;
}

interface DbInventoryHealthMetrics {
  id: string;
  tenant_id: string;
  product_id: string;
  store_id: string;
  metric_date: string;
  opening_stock: number;
  closing_stock: number;
  min_stock: number | null;
  max_stock: number | null;
  units_sold: number;
  units_received: number;
  units_adjusted: number;
  units_returned: number;
  days_of_stock: number | null;
  turnover_rate: number | null;
  fill_rate: number | null;
  is_overstock: boolean;
  is_understock: boolean;
  is_deadstock: boolean;
  is_at_risk: boolean;
  stock_value: number | null;
  cost_of_holding: number | null;
  created_at: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert PostgreSQL NUMERIC/DECIMAL (returned as string) to number
 */
function toNumber(val: string | number | null | undefined): number {
  if (val === null || val === undefined) return 0;
  return typeof val === 'string' ? parseFloat(val) : val;
}

function toNumberOrNull(val: string | number | null | undefined): number | undefined {
  if (val === null || val === undefined) return undefined;
  return typeof val === 'string' ? parseFloat(val) : val;
}

// ============================================
// MAPPING FUNCTIONS
// ============================================

function mapDbToStockoutEvent(row: DbStockoutEvent): StockoutEvent {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    productId: row.product_id,
    storeId: row.store_id,
    stockoutStart: row.stockout_start,
    stockoutEnd: row.stockout_end ?? undefined,
    durationHours: toNumberOrNull(row.duration_hours),
    isResolved: row.is_resolved,
    estimatedLostSales: toNumberOrNull(row.estimated_lost_sales),
    estimatedLostUnits: toNumberOrNull(row.estimated_lost_units),
    customerImpactCount: toNumber(row.customer_impact_count),
    rootCause: (row.root_cause as StockoutRootCause) ?? undefined,
    rootCauseNotes: row.root_cause_notes ?? undefined,
    linkedPoId: row.linked_po_id ?? undefined,
    linkedCaseId: row.linked_case_id ?? undefined,
    resolvedBy: row.resolved_by ?? undefined,
    resolutionNotes: row.resolution_notes ?? undefined,
    metadata: row.metadata,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapDbToStockoutPrediction(row: DbStockoutPrediction): StockoutPrediction {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    productId: row.product_id,
    storeId: row.store_id,
    predictionDate: row.prediction_date,
    predictedStockoutDate: row.predicted_stockout_date ?? undefined,
    daysUntilStockout: toNumberOrNull(row.days_until_stockout),
    probability: toNumber(row.probability),
    currentStock: toNumber(row.current_stock),
    dailySalesAvg: toNumber(row.daily_sales_avg),
    dailySalesStddev: toNumberOrNull(row.daily_sales_stddev),
    leadTimeDays: row.lead_time_days ?? undefined,
    pendingPoUnits: toNumber(row.pending_po_units),
    expectedPoArrival: row.expected_po_arrival ?? undefined,
    confidenceLevel: row.confidence_level as ConfidenceLevel,
    confidenceScore: toNumberOrNull(row.confidence_score),
    modelVersion: row.model_version ?? undefined,
    modelType: row.model_type as PredictionModelType,
    riskLevel: row.risk_level as PredictionRiskLevel,
    isActive: row.is_active,
    wasAccurate: row.was_accurate ?? undefined,
    actualStockoutDate: row.actual_stockout_date ?? undefined,
    alertGenerated: row.alert_generated,
    alertId: row.alert_id ?? undefined,
    createdAt: row.created_at,
  };
}

function mapDbToAnomalyScore(row: DbAnomalyScore): AnomalyScore {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    entityType: row.entity_type as AnomalyEntityType,
    entityId: row.entity_id,
    scoreDate: row.score_date,
    anomalyScore: toNumber(row.anomaly_score),
    anomalyType: row.anomaly_type ?? undefined,
    anomalyCategory: (row.anomaly_category as AnomalyCategory) ?? undefined,
    baselineValue: toNumberOrNull(row.baseline_value),
    observedValue: toNumberOrNull(row.observed_value),
    deviationPercentage: toNumberOrNull(row.deviation_percentage),
    zScore: toNumberOrNull(row.z_score),
    metricName: row.metric_name ?? undefined,
    modelVersion: row.model_version ?? undefined,
    detectionMethod: row.detection_method ?? undefined,
    severity: row.severity as AnomalySeverity,
    requiresInvestigation: row.requires_investigation,
    investigationStatus: row.investigation_status as AnomalyInvestigationStatus,
    investigatedBy: row.investigated_by ?? undefined,
    linkedAlertId: row.linked_alert_id ?? undefined,
    linkedCaseId: row.linked_case_id ?? undefined,
    metadata: row.metadata,
    createdAt: row.created_at,
  };
}

function mapDbToDemandForecast(row: DbDemandForecast): DemandForecast {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    productId: row.product_id,
    storeId: row.store_id ?? undefined,
    forecastDate: row.forecast_date,
    forecastHorizonDays: toNumber(row.forecast_horizon_days),
    predictedDailyDemand: toNumber(row.predicted_daily_demand),
    predictedWeeklyDemand: toNumberOrNull(row.predicted_weekly_demand),
    predictedMonthlyDemand: toNumberOrNull(row.predicted_monthly_demand),
    demandLowerBound: toNumberOrNull(row.demand_lower_bound),
    demandUpperBound: toNumberOrNull(row.demand_upper_bound),
    confidenceInterval: toNumber(row.confidence_interval),
    seasonalityFactor: toNumber(row.seasonality_factor),
    isPromotionalPeriod: row.is_promotional_period,
    actualDemand: toNumberOrNull(row.actual_demand),
    forecastError: toNumberOrNull(row.forecast_error),
    mape: toNumberOrNull(row.mape),
    modelVersion: row.model_version ?? undefined,
    modelType: row.model_type ?? undefined,
    createdAt: row.created_at,
  };
}

function mapDbToInventoryHealthMetrics(row: DbInventoryHealthMetrics): InventoryHealthMetrics {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    productId: row.product_id,
    storeId: row.store_id,
    metricDate: row.metric_date,
    openingStock: toNumber(row.opening_stock),
    closingStock: toNumber(row.closing_stock),
    minStock: toNumberOrNull(row.min_stock),
    maxStock: toNumberOrNull(row.max_stock),
    unitsSold: toNumber(row.units_sold),
    unitsReceived: toNumber(row.units_received),
    unitsAdjusted: toNumber(row.units_adjusted),
    unitsReturned: toNumber(row.units_returned),
    daysOfStock: toNumberOrNull(row.days_of_stock),
    turnoverRate: toNumberOrNull(row.turnover_rate),
    fillRate: toNumberOrNull(row.fill_rate),
    isOverstock: row.is_overstock,
    isUnderstock: row.is_understock,
    isDeadstock: row.is_deadstock,
    isAtRisk: row.is_at_risk,
    stockValue: toNumberOrNull(row.stock_value),
    costOfHolding: toNumberOrNull(row.cost_of_holding),
    createdAt: row.created_at,
  };
}

// ============================================
// PREDICTION OPTIONS
// ============================================

interface PredictionOptions {
  storeId?: string;
  horizonDays?: number;
  minDailySales?: number;
  abcClassFilter?: ABCClass[];
}

// ============================================
// STOCKOUT PREDICTION SERVICE
// ============================================

export const stockoutPredictionService = {
  // ============================================
  // STOCKOUT EVENTS - CRUD
  // ============================================

  /**
   * Record a new stockout event
   */
  async recordStockout(
    tenantId: string,
    productId: string,
    storeId: string,
    options?: {
      estimatedLostSales?: number;
      estimatedLostUnits?: number;
      linkedPoId?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<StockoutEvent> {
    const { data, error } = await supabase
      .from('stockout_events')
      .insert({
        tenant_id: tenantId,
        product_id: productId,
        store_id: storeId,
        stockout_start: new Date().toISOString(),
        estimated_lost_sales: options?.estimatedLostSales,
        estimated_lost_units: options?.estimatedLostUnits,
        linked_po_id: options?.linkedPoId,
        metadata: options?.metadata ?? {},
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to record stockout: ${error.message}`);
    return mapDbToStockoutEvent(data as DbStockoutEvent);
  },

  /**
   * Resolve a stockout event
   */
  async resolveStockout(
    eventId: string,
    resolvedBy: string,
    options?: {
      rootCause?: StockoutRootCause;
      rootCauseNotes?: string;
      resolutionNotes?: string;
      linkedCaseId?: string;
    }
  ): Promise<StockoutEvent> {
    const { data, error } = await supabase
      .from('stockout_events')
      .update({
        stockout_end: new Date().toISOString(),
        resolved_by: resolvedBy,
        root_cause: options?.rootCause,
        root_cause_notes: options?.rootCauseNotes,
        resolution_notes: options?.resolutionNotes,
        linked_case_id: options?.linkedCaseId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', eventId)
      .select()
      .single();

    if (error) throw new Error(`Failed to resolve stockout: ${error.message}`);
    return mapDbToStockoutEvent(data as DbStockoutEvent);
  },

  /**
   * Get stockout event by ID
   */
  async getStockoutEvent(eventId: string): Promise<StockoutEvent | null> {
    const { data, error } = await supabase
      .from('stockout_events')
      .select('*')
      .eq('id', eventId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error(`Failed to get stockout event: ${error.message}`);
    }
    return mapDbToStockoutEvent(data as DbStockoutEvent);
  },

  /**
   * Get active (unresolved) stockouts
   */
  async getActiveStockouts(
    tenantId: string,
    options?: {
      storeId?: string;
      abcClass?: ABCClass;
      limit?: number;
    }
  ): Promise<ActiveStockout[]> {
    // Use the active_stockouts view which joins product and store info
    let query = supabase
      .from('active_stockouts')
      .select('*')
      .eq('tenant_id', tenantId);

    if (options?.storeId) {
      query = query.eq('store_id', options.storeId);
    }

    if (options?.abcClass) {
      query = query.eq('abc_class', options.abcClass);
    }

    query = query.order('stockout_start', { ascending: false });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get active stockouts: ${error.message}`);

    return (data ?? []).map((row) => ({
      ...mapDbToStockoutEvent(row as unknown as DbStockoutEvent),
      sku: row.sku,
      productName: row.product_name,
      abcClass: row.abc_class as ABCClass | undefined,
      storeName: row.store_name,
      hoursActive: row.hours_active,
    }));
  },

  /**
   * Get stockout events for a date range
   */
  async getStockoutEvents(
    tenantId: string,
    dateRange: DateRange,
    options?: {
      storeId?: string;
      productId?: string;
      rootCause?: StockoutRootCause;
      resolved?: boolean;
    }
  ): Promise<StockoutEvent[]> {
    let query = supabase
      .from('stockout_events')
      .select('*')
      .eq('tenant_id', tenantId)
      .gte('stockout_start', dateRange.start)
      .lte('stockout_start', dateRange.end);

    if (options?.storeId) {
      query = query.eq('store_id', options.storeId);
    }

    if (options?.productId) {
      query = query.eq('product_id', options.productId);
    }

    if (options?.rootCause) {
      query = query.eq('root_cause', options.rootCause);
    }

    if (options?.resolved !== undefined) {
      query = query.eq('is_resolved', options.resolved);
    }

    query = query.order('stockout_start', { ascending: false });

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get stockout events: ${error.message}`);
    return (data ?? []).map((row) => mapDbToStockoutEvent(row as DbStockoutEvent));
  },

  // ============================================
  // STOCKOUT PREDICTIONS
  // ============================================

  /**
   * Run stockout prediction for a tenant (rule-based)
   * Uses the database function or calculates in TypeScript
   */
  async predictStockouts(
    tenantId: string,
    options?: PredictionOptions
  ): Promise<StockoutPredictionResult> {
    const horizonDays = options?.horizonDays ?? 14;
    const minDailySales = options?.minDailySales ?? 0.1;

    console.log('[predictStockouts] Starting for tenant:', tenantId, 'options:', options);

    // Get current inventory with product info
    // Note: abc_class column may not exist in all deployments
    let productQuery = supabase
      .from('products')
      .select(`
        id,
        tenant_id,
        sku,
        name,
        lead_time_days,
        reorder_point,
        category,
        inventory!inner(store_id, system_qty)
      `)
      .eq('tenant_id', tenantId);

    if (options?.storeId) {
      productQuery = productQuery.eq('inventory.store_id', options.storeId);
    }

    // abc_class filter disabled - column may not exist in all deployments
    // if (options?.abcClassFilter && options.abcClassFilter.length > 0) {
    //   productQuery = productQuery.in('abc_class', options.abcClassFilter);
    // }

    const { data: products, error: productsError } = await productQuery;

    if (productsError) {
      console.error('[predictStockouts] Products query error:', productsError);
      throw new Error(`Failed to fetch products: ${productsError.message}`);
    }

    console.log('[predictStockouts] Found products with inventory:', products?.length ?? 0);

    // Get sales data for last 30 days to calculate velocity
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: salesData, error: salesError } = await supabase
      .from('transactions')
      .select('product_id, store_id, quantity')
      .eq('tenant_id', tenantId)
      .eq('type', 'SALE')
      .gte('created_at', thirtyDaysAgo.toISOString());

    if (salesError) {
      console.error('[predictStockouts] Sales query error:', salesError);
      throw new Error(`Failed to fetch sales data: ${salesError.message}`);
    }

    console.log('[predictStockouts] Found sales transactions:', salesData?.length ?? 0);

    // Aggregate sales by product/store
    const salesMap = new Map<string, { totalQty: number; days: Set<string> }>();
    for (const sale of salesData ?? []) {
      const key = `${sale.product_id}:${sale.store_id}`;
      if (!salesMap.has(key)) {
        salesMap.set(key, { totalQty: 0, days: new Set() });
      }
      const entry = salesMap.get(key)!;
      entry.totalQty += Math.abs(sale.quantity);
      // Track unique days for std dev calculation
      entry.days.add(new Date().toISOString().split('T')[0]);
    }

    // Get pending POs (optional - table may not exist in all deployments)
    const pendingPoMap = new Map<string, { units: number; earliestArrival?: string }>();
    try {
      const { data: pendingPOs, error: poError } = await supabase
        .from('po_line_items')
        .select(`
          product_id,
          quantity_ordered,
          quantity_received,
          purchase_orders!inner(store_id, status, expected_arrival)
        `)
        .in('purchase_orders.status', ['submitted', 'confirmed', 'in_transit']);

      if (!poError && pendingPOs) {
        // Aggregate pending PO units by product/store
        for (const line of pendingPOs) {
          const po = line.purchase_orders as unknown as { store_id: string; expected_arrival: string | null };
          const key = `${line.product_id}:${po.store_id}`;
          const pendingUnits = line.quantity_ordered - line.quantity_received;
          if (!pendingPoMap.has(key)) {
            pendingPoMap.set(key, { units: 0 });
          }
          const entry = pendingPoMap.get(key)!;
          entry.units += pendingUnits;
          if (po.expected_arrival) {
            if (!entry.earliestArrival || po.expected_arrival < entry.earliestArrival) {
              entry.earliestArrival = po.expected_arrival;
            }
          }
        }
      }
    } catch {
      // po_line_items table may not exist - continue without PO data
      console.log('[predictStockouts] PO data not available, continuing without it');
    }

    // Calculate predictions
    const predictions: Array<{
      tenantId: string;
      productId: string;
      storeId: string;
      currentStock: number;
      dailySalesAvg: number;
      daysUntilStockout: number | null;
      probability: number;
      riskLevel: PredictionRiskLevel;
      confidenceLevel: ConfidenceLevel;
      pendingPoUnits: number;
      expectedPoArrival?: string;
      leadTimeDays?: number;
    }> = [];

    for (const product of products ?? []) {
      const inventoryRecords = product.inventory as unknown as Array<{ store_id: string; system_qty: number }>;

      for (const inv of inventoryRecords) {
        const key = `${product.id}:${inv.store_id}`;
        const salesEntry = salesMap.get(key);
        const pendingPo = pendingPoMap.get(key);

        // Calculate daily sales average
        const totalSales = salesEntry?.totalQty ?? 0;
        const dailySalesAvg = totalSales / 30;

        const currentStock = toNumber(inv.system_qty);
        const pendingUnits = pendingPo?.units ?? 0;

        // Calculate days until stockout
        let daysUntilStockout: number | null = null;

        if (dailySalesAvg >= minDailySales) {
          // Have sales data - use actual velocity
          daysUntilStockout = currentStock / dailySalesAvg;
        } else if (currentStock <= 20) {
          // Low stock with no/low sales - estimate conservatively
          // Assume 2 units/day as baseline for flagging low stock
          daysUntilStockout = currentStock / 2;
        }

        // Only create prediction if stockout within horizon
        if (daysUntilStockout !== null && daysUntilStockout <= horizonDays) {
          // Calculate probability and risk level
          let probability: number;
          let riskLevel: PredictionRiskLevel;

          if (daysUntilStockout <= 0) {
            probability = 1.0;
            riskLevel = 'critical';
          } else if (daysUntilStockout <= 3 && pendingUnits === 0) {
            probability = 0.95;
            riskLevel = 'critical';
          } else if (daysUntilStockout <= 7) {
            probability = 0.75;
            riskLevel = 'high';
          } else if (daysUntilStockout <= 14) {
            probability = 0.50;
            riskLevel = 'medium';
          } else {
            probability = 0.25;
            riskLevel = 'low';
          }

          // Adjust probability if PO is expected before stockout
          if (pendingPo?.earliestArrival) {
            const arrivalDate = new Date(pendingPo.earliestArrival);
            const stockoutDate = new Date();
            stockoutDate.setDate(stockoutDate.getDate() + Math.floor(daysUntilStockout));

            if (arrivalDate < stockoutDate) {
              // PO expected before stockout - reduce probability
              probability *= 0.5;
              if (riskLevel === 'critical') riskLevel = 'high';
              else if (riskLevel === 'high') riskLevel = 'medium';
            }
          }

          // Confidence level based on sales variance
          const confidenceLevel: ConfidenceLevel = salesEntry?.days.size && salesEntry.days.size >= 20
            ? 'high'
            : salesEntry?.days.size && salesEntry.days.size >= 10
              ? 'medium'
              : 'low';

          predictions.push({
            tenantId,
            productId: product.id,
            storeId: inv.store_id,
            currentStock,
            dailySalesAvg,
            daysUntilStockout,
            probability,
            riskLevel,
            confidenceLevel,
            pendingPoUnits: pendingUnits,
            expectedPoArrival: pendingPo?.earliestArrival,
            leadTimeDays: product.lead_time_days,
          });
        }
      }
    }

    console.log('[predictStockouts] Generated predictions:', predictions.length);
    if (predictions.length > 0) {
      console.log('[predictStockouts] Sample prediction:', predictions[0]);
    }

    // Delete existing predictions for today
    const today = new Date().toISOString().split('T')[0];
    const { error: deleteError } = await supabase
      .from('stockout_predictions')
      .delete()
      .eq('tenant_id', tenantId)
      .eq('prediction_date', today);

    if (deleteError) {
      console.error('[predictStockouts] Delete error:', deleteError);
    }

    // Upsert predictions (handles duplicate key on product_id, store_id, prediction_date)
    if (predictions.length > 0) {
      const { error: upsertError } = await supabase
        .from('stockout_predictions')
        .upsert(
          predictions.map((p) => ({
            tenant_id: p.tenantId,
            product_id: p.productId,
            store_id: p.storeId,
            prediction_date: today,
            predicted_stockout_date: p.daysUntilStockout !== null
              ? new Date(Date.now() + p.daysUntilStockout * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
              : null,
            days_until_stockout: p.daysUntilStockout,
            probability: p.probability,
            current_stock: p.currentStock,
            daily_sales_avg: p.dailySalesAvg,
            lead_time_days: p.leadTimeDays,
            pending_po_units: p.pendingPoUnits,
            expected_po_arrival: p.expectedPoArrival,
            confidence_level: p.confidenceLevel,
            risk_level: p.riskLevel,
            model_type: 'rule_based',
            model_version: '1.0.0',
            is_active: true,
          })),
          { onConflict: 'product_id,store_id,prediction_date' }
        );

      if (upsertError) {
        console.error('[predictStockouts] Upsert error:', upsertError);
        throw new Error(`Failed to upsert predictions: ${upsertError.message}`);
      }
      console.log('[predictStockouts] Upserted', predictions.length, 'predictions successfully');
    }

    // Count by risk level
    const criticalCount = predictions.filter((p) => p.riskLevel === 'critical').length;
    const highCount = predictions.filter((p) => p.riskLevel === 'high').length;
    const mediumCount = predictions.filter((p) => p.riskLevel === 'medium').length;
    const lowCount = predictions.filter((p) => p.riskLevel === 'low').length;

    return {
      predictionsCreated: predictions.length,
      criticalCount,
      highCount,
      mediumCount,
      lowCount,
      executedAt: new Date().toISOString(),
    };
  },

  /**
   * Get high-risk stockout predictions (critical and high)
   * Auto-calculates predictions if none exist for today
   */
  async getHighRiskPredictions(
    tenantId: string,
    options?: {
      storeId?: string;
      abcClass?: ABCClass;
      minRiskLevel?: PredictionRiskLevel;
      limit?: number;
    }
  ): Promise<HighRiskStockoutPrediction[]> {
    // First check if predictions exist for today
    const today = new Date().toISOString().split('T')[0];
    const { count } = await supabase
      .from('stockout_predictions')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('prediction_date', today)
      .eq('is_active', true);

    // If no predictions for today, calculate them first
    if (!count || count === 0) {
      console.log('[StockoutPredictionService] No predictions for today, calculating...');
      try {
        const result = await this.predictStockouts(tenantId, {
          storeId: options?.storeId,
          horizonDays: 14,
          minDailySales: 0.01, // Lower threshold to catch more products
        });
        console.log('[StockoutPredictionService] Predictions calculated:', result);
      } catch (err) {
        console.error('[StockoutPredictionService] Failed to calculate predictions:', err);
        // Continue to try fetching any existing data
      }
    } else {
      console.log('[StockoutPredictionService] Found existing predictions:', count);
    }

    // Use the high_risk_stockout_predictions view
    let query = supabase
      .from('high_risk_stockout_predictions')
      .select('*')
      .eq('tenant_id', tenantId);

    if (options?.storeId) {
      query = query.eq('store_id', options.storeId);
    }

    // abc_class filter disabled - column may not exist in all deployments
    // if (options?.abcClass) {
    //   query = query.eq('abc_class', options.abcClass);
    // }

    if (options?.minRiskLevel) {
      const riskLevels: PredictionRiskLevel[] = ['critical', 'high', 'medium', 'low'];
      const minIndex = riskLevels.indexOf(options.minRiskLevel);
      const acceptableLevels = riskLevels.slice(0, minIndex + 1);
      query = query.in('risk_level', acceptableLevels);
    }

    query = query.order('days_until_stockout', { ascending: true });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    console.log('[getHighRiskPredictions] View query result:', { data: data?.length, error });

    if (error) {
      console.log('[getHighRiskPredictions] View error, trying fallback:', error.code, error.message);
      // If view doesn't exist or any error, fall back to manual query
      return this.getHighRiskPredictionsFallback(tenantId, options);
    }

    // If no data from view, try fallback
    if (!data || data.length === 0) {
      console.log('[getHighRiskPredictions] No data from view, trying fallback');
      return this.getHighRiskPredictionsFallback(tenantId, options);
    }

    return (data ?? []).map((row) => ({
      ...mapDbToStockoutPrediction(row as unknown as DbStockoutPrediction),
      sku: row.sku,
      productName: row.product_name,
      abcClass: (row as Record<string, unknown>).abc_class as ABCClass | undefined, // may not exist
      category: row.category,
      storeName: row.store_name,
    }));
  },

  /**
   * Fallback method - directly queries low-stock inventory items
   * This bypasses stockout_predictions table and calculates risk on-the-fly
   */
  async getHighRiskPredictionsFallback(
    tenantId: string,
    options?: {
      storeId?: string;
      abcClass?: ABCClass;
      minRiskLevel?: PredictionRiskLevel;
      limit?: number;
    }
  ): Promise<HighRiskStockoutPrediction[]> {
    console.log('[getHighRiskPredictionsFallback] Starting direct inventory query for tenant:', tenantId, 'store:', options?.storeId);

    // Query inventory directly with product and store info
    // Note: abc_class column may not exist in all deployments
    // Shows items with lowest stock levels (ascending order, limited to 10)
    let query = supabase
      .from('inventory')
      .select(`
        id,
        tenant_id,
        store_id,
        product_id,
        system_qty,
        physical_qty,
        products!inner(id, sku, name, category, lead_time_days),
        stores!inner(id, name)
      `)
      .eq('tenant_id', tenantId)
      .order('system_qty', { ascending: true })
      .limit(10); // Get 10 lowest stock items regardless of level

    // Don't filter by store initially - let's see all low stock across tenant
    // if (options?.storeId) {
    //   query = query.eq('store_id', options.storeId);
    // }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    console.log('[getHighRiskPredictionsFallback] Inventory query result:', {
      count: data?.length,
      error,
      firstItem: data?.[0],
      tenantId
    });

    if (error) {
      console.error('[getHighRiskPredictionsFallback] Query Error:', error.message, error.code, error.details);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('[getHighRiskPredictionsFallback] No inventory items found for tenant:', tenantId);
      return [];
    }

    console.log('[getHighRiskPredictionsFallback] Found', data.length, 'inventory items, lowest stock:', toNumber((data[0] as { system_qty: number }).system_qty));

    // Convert inventory items to prediction format
    // Since we're showing the lowest stock items, assign risk based on position in sorted list
    const predictions: HighRiskStockoutPrediction[] = (data ?? []).map((inv, index) => {
      const product = inv.products as unknown as { id: string; sku: string; name: string; category: string | null; lead_time_days: number | null };
      const store = inv.stores as unknown as { id: string; name: string };
      const currentStock = toNumber(inv.system_qty);

      // Estimate risk based on position in lowest-stock list
      // First 2 items = critical, next 3 = high, next 3 = medium, rest = low
      let riskLevel: PredictionRiskLevel;
      let probability: number;
      let daysUntilStockout: number;

      // Assume average daily sales of 5 units for estimation
      const estimatedDailySales = 5;
      daysUntilStockout = currentStock / estimatedDailySales;

      if (index < 2) {
        riskLevel = 'critical';
        probability = 0.85;
      } else if (index < 5) {
        riskLevel = 'high';
        probability = 0.65;
      } else if (index < 8) {
        riskLevel = 'medium';
        probability = 0.45;
      } else {
        riskLevel = 'low';
        probability = 0.25;
      }

      // Filter by minRiskLevel if specified
      if (options?.minRiskLevel) {
        const riskOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        if (riskOrder[riskLevel] > riskOrder[options.minRiskLevel]) {
          return null;
        }
      }

      return {
        id: inv.id,
        tenantId: inv.tenant_id,
        productId: product.id,
        storeId: store.id,
        predictionDate: new Date().toISOString().split('T')[0],
        predictedStockoutDate: new Date(Date.now() + daysUntilStockout * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        daysUntilStockout,
        probability,
        currentStock,
        dailySalesAvg: 2, // Estimated
        confidenceLevel: 'low' as ConfidenceLevel,
        riskLevel,
        modelType: 'rule_based' as PredictionModelType,
        isActive: true,
        alertGenerated: false,
        createdAt: new Date().toISOString(),
        // Extended fields
        sku: product.sku,
        productName: product.name,
        abcClass: undefined, // abc_class column may not exist in all deployments
        category: product.category ?? undefined,
        storeName: store.name,
      } as HighRiskStockoutPrediction;
    }).filter((p): p is HighRiskStockoutPrediction => p !== null);

    console.log('[getHighRiskPredictionsFallback] Generated', predictions.length, 'predictions from inventory');
    return predictions;
  },

  /**
   * Get predictions for a specific product
   */
  async getPredictionsForProduct(
    productId: string,
    options?: {
      storeId?: string;
      limit?: number;
    }
  ): Promise<StockoutPrediction[]> {
    let query = supabase
      .from('stockout_predictions')
      .select('*')
      .eq('product_id', productId)
      .eq('is_active', true);

    if (options?.storeId) {
      query = query.eq('store_id', options.storeId);
    }

    query = query.order('prediction_date', { ascending: false });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get predictions: ${error.message}`);
    return (data ?? []).map((row) => mapDbToStockoutPrediction(row as DbStockoutPrediction));
  },

  /**
   * Mark a prediction as accurate or inaccurate (for model training)
   */
  async markPredictionAccuracy(
    predictionId: string,
    wasAccurate: boolean,
    actualStockoutDate?: string
  ): Promise<StockoutPrediction> {
    const { data, error } = await supabase
      .from('stockout_predictions')
      .update({
        was_accurate: wasAccurate,
        actual_stockout_date: actualStockoutDate,
        is_active: false,
      })
      .eq('id', predictionId)
      .select()
      .single();

    if (error) throw new Error(`Failed to update prediction: ${error.message}`);
    return mapDbToStockoutPrediction(data as DbStockoutPrediction);
  },

  // ============================================
  // ANOMALY DETECTION
  // ============================================

  /**
   * Get anomaly scores for an entity type
   */
  async getAnomalyScores(
    tenantId: string,
    entityType: AnomalyEntityType,
    options?: {
      dateRange?: DateRange;
      minSeverity?: AnomalySeverity;
      requiresInvestigation?: boolean;
      limit?: number;
    }
  ): Promise<AnomalyScore[]> {
    let query = supabase
      .from('anomaly_scores')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('entity_type', entityType);

    if (options?.dateRange) {
      query = query
        .gte('score_date', options.dateRange.start)
        .lte('score_date', options.dateRange.end);
    }

    if (options?.minSeverity) {
      const severityOrder = ['low', 'medium', 'high', 'critical'];
      const minIndex = severityOrder.indexOf(options.minSeverity);
      const acceptableSeverities = severityOrder.slice(minIndex);
      query = query.in('severity', acceptableSeverities);
    }

    if (options?.requiresInvestigation !== undefined) {
      query = query.eq('requires_investigation', options.requiresInvestigation);
    }

    query = query.order('anomaly_score', { ascending: false });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get anomaly scores: ${error.message}`);
    return (data ?? []).map((row) => mapDbToAnomalyScore(row as DbAnomalyScore));
  },

  /**
   * Get anomalies for a specific entity
   */
  async getEntityAnomalies(
    entityType: AnomalyEntityType,
    entityId: string,
    options?: {
      limit?: number;
    }
  ): Promise<AnomalyScore[]> {
    let query = supabase
      .from('anomaly_scores')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('score_date', { ascending: false });

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get entity anomalies: ${error.message}`);
    return (data ?? []).map((row) => mapDbToAnomalyScore(row as DbAnomalyScore));
  },

  /**
   * Update anomaly investigation status
   */
  async updateAnomalyInvestigation(
    anomalyId: string,
    status: AnomalyInvestigationStatus,
    investigatedBy?: string,
    linkedAlertId?: string,
    linkedCaseId?: string
  ): Promise<AnomalyScore> {
    const { data, error } = await supabase
      .from('anomaly_scores')
      .update({
        investigation_status: status,
        investigated_by: investigatedBy,
        linked_alert_id: linkedAlertId,
        linked_case_id: linkedCaseId,
      })
      .eq('id', anomalyId)
      .select()
      .single();

    if (error) throw new Error(`Failed to update anomaly: ${error.message}`);
    return mapDbToAnomalyScore(data as DbAnomalyScore);
  },

  // ============================================
  // DEMAND FORECASTING
  // ============================================

  /**
   * Get demand forecast for a product
   */
  async getDemandForecast(
    productId: string,
    options?: {
      storeId?: string;
      limit?: number;
    }
  ): Promise<DemandForecast[]> {
    let query = supabase
      .from('demand_forecasts')
      .select('*')
      .eq('product_id', productId)
      .order('forecast_date', { ascending: false });

    if (options?.storeId) {
      query = query.eq('store_id', options.storeId);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get demand forecast: ${error.message}`);
    return (data ?? []).map((row) => mapDbToDemandForecast(row as DbDemandForecast));
  },

  /**
   * Create a demand forecast (rule-based)
   */
  async createDemandForecast(
    tenantId: string,
    productId: string,
    storeId: string | null,
    horizonDays: number = 30
  ): Promise<DemandForecast> {
    // Calculate average daily demand from last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let salesQuery = supabase
      .from('transactions')
      .select('quantity')
      .eq('product_id', productId)
      .eq('type', 'SALE')
      .gte('created_at', thirtyDaysAgo.toISOString());

    if (storeId) {
      salesQuery = salesQuery.eq('store_id', storeId);
    }

    const { data: sales, error: salesError } = await salesQuery;

    if (salesError) throw new Error(`Failed to fetch sales: ${salesError.message}`);

    const totalSales = (sales ?? []).reduce((sum, s) => sum + Math.abs(s.quantity), 0);
    const dailyDemand = totalSales / 30;

    // Simple seasonal factor (could be enhanced with actual seasonal analysis)
    const seasonalityFactor = 1.0;

    const forecast = {
      tenant_id: tenantId,
      product_id: productId,
      store_id: storeId,
      forecast_date: new Date().toISOString().split('T')[0],
      forecast_horizon_days: horizonDays,
      predicted_daily_demand: dailyDemand * seasonalityFactor,
      predicted_weekly_demand: dailyDemand * 7 * seasonalityFactor,
      predicted_monthly_demand: dailyDemand * 30 * seasonalityFactor,
      demand_lower_bound: dailyDemand * 0.8,
      demand_upper_bound: dailyDemand * 1.2,
      confidence_interval: 95.0,
      seasonality_factor: seasonalityFactor,
      is_promotional_period: false,
      model_version: '1.0.0',
      model_type: 'rule_based',
    };

    const { data, error } = await supabase
      .from('demand_forecasts')
      .upsert(forecast, { onConflict: 'product_id,store_id,forecast_date' })
      .select()
      .single();

    if (error) throw new Error(`Failed to create forecast: ${error.message}`);
    return mapDbToDemandForecast(data as DbDemandForecast);
  },

  // ============================================
  // INVENTORY HEALTH METRICS
  // ============================================

  /**
   * Get inventory health metrics for a product
   */
  async getInventoryHealthMetrics(
    productId: string,
    storeId: string,
    options?: {
      dateRange?: DateRange;
      limit?: number;
    }
  ): Promise<InventoryHealthMetrics[]> {
    let query = supabase
      .from('inventory_health_metrics')
      .select('*')
      .eq('product_id', productId)
      .eq('store_id', storeId)
      .order('metric_date', { ascending: false });

    if (options?.dateRange) {
      query = query
        .gte('metric_date', options.dateRange.start)
        .lte('metric_date', options.dateRange.end);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get health metrics: ${error.message}`);
    return (data ?? []).map((row) => mapDbToInventoryHealthMetrics(row as DbInventoryHealthMetrics));
  },

  /**
   * Get at-risk inventory items
   */
  async getAtRiskInventory(
    tenantId: string,
    options?: {
      storeId?: string;
      riskType?: 'overstock' | 'understock' | 'deadstock' | 'at_risk';
      limit?: number;
    }
  ): Promise<InventoryHealthMetrics[]> {
    const today = new Date().toISOString().split('T')[0];

    let query = supabase
      .from('inventory_health_metrics')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('metric_date', today);

    if (options?.storeId) {
      query = query.eq('store_id', options.storeId);
    }

    // Filter by risk type
    switch (options?.riskType) {
      case 'overstock':
        query = query.eq('is_overstock', true);
        break;
      case 'understock':
        query = query.eq('is_understock', true);
        break;
      case 'deadstock':
        query = query.eq('is_deadstock', true);
        break;
      case 'at_risk':
      default:
        query = query.eq('is_at_risk', true);
        break;
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to get at-risk inventory: ${error.message}`);
    return (data ?? []).map((row) => mapDbToInventoryHealthMetrics(row as DbInventoryHealthMetrics));
  },

  // ============================================
  // ANALYTICS & METRICS
  // ============================================

  /**
   * Calculate stockout rate for a date range
   */
  async calculateStockoutRate(
    tenantId: string,
    dateRange: DateRange
  ): Promise<number> {
    // Get total order lines
    const { count: totalOrders, error: ordersError } = await supabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('type', 'SALE')
      .gte('created_at', dateRange.start)
      .lte('created_at', dateRange.end);

    if (ordersError) throw new Error(`Failed to count orders: ${ordersError.message}`);

    // Get stockout events count
    const { count: stockoutCount, error: stockoutError } = await supabase
      .from('stockout_events')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .gte('stockout_start', dateRange.start)
      .lte('stockout_start', dateRange.end);

    if (stockoutError) throw new Error(`Failed to count stockouts: ${stockoutError.message}`);

    if (!totalOrders || totalOrders === 0) return 0;

    // Stockout rate = stockout events / total orders * 100
    return ((stockoutCount ?? 0) / totalOrders) * 100;
  },

  /**
   * Get stockout root cause breakdown
   */
  async getStockoutRootCauseBreakdown(
    tenantId: string,
    dateRange: DateRange
  ): Promise<Array<{ cause: StockoutRootCause; count: number; percentage: number }>> {
    const { data, error } = await supabase
      .from('stockout_events')
      .select('root_cause')
      .eq('tenant_id', tenantId)
      .gte('stockout_start', dateRange.start)
      .lte('stockout_start', dateRange.end)
      .not('root_cause', 'is', null);

    if (error) throw new Error(`Failed to get root causes: ${error.message}`);

    // Count by root cause
    const counts = new Map<string, number>();
    for (const row of data ?? []) {
      const cause = row.root_cause ?? 'unknown';
      counts.set(cause, (counts.get(cause) ?? 0) + 1);
    }

    const total = (data ?? []).length;

    return Array.from(counts.entries())
      .map(([cause, count]) => ({
        cause: cause as StockoutRootCause,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  },

  /**
   * Estimate lost sales from stockouts
   */
  async estimateLostSales(
    tenantId: string,
    dateRange: DateRange
  ): Promise<number> {
    const { data, error } = await supabase
      .from('stockout_events')
      .select('estimated_lost_sales')
      .eq('tenant_id', tenantId)
      .gte('stockout_start', dateRange.start)
      .lte('stockout_start', dateRange.end);

    if (error) throw new Error(`Failed to get lost sales: ${error.message}`);

    return (data ?? []).reduce((sum, row) => sum + (row.estimated_lost_sales ?? 0), 0);
  },

  /**
   * Get prediction accuracy metrics
   */
  async getPredictionAccuracyMetrics(
    tenantId: string,
    dateRange: DateRange
  ): Promise<{
    totalPredictions: number;
    accuratePredictions: number;
    inaccuratePredictions: number;
    pendingValidation: number;
    accuracyRate: number;
  }> {
    const { data, error } = await supabase
      .from('stockout_predictions')
      .select('was_accurate')
      .eq('tenant_id', tenantId)
      .gte('prediction_date', dateRange.start)
      .lte('prediction_date', dateRange.end);

    if (error) throw new Error(`Failed to get accuracy metrics: ${error.message}`);

    const totalPredictions = (data ?? []).length;
    const accuratePredictions = (data ?? []).filter((p) => p.was_accurate === true).length;
    const inaccuratePredictions = (data ?? []).filter((p) => p.was_accurate === false).length;
    const pendingValidation = (data ?? []).filter((p) => p.was_accurate === null).length;

    const validatedCount = accuratePredictions + inaccuratePredictions;
    const accuracyRate = validatedCount > 0 ? (accuratePredictions / validatedCount) * 100 : 0;

    return {
      totalPredictions,
      accuratePredictions,
      inaccuratePredictions,
      pendingValidation,
      accuracyRate,
    };
  },
};

export default stockoutPredictionService;

// ── Effective stockout (PRD-4 "quiebre fantasma", Orlando 2026-06-02) ─────────
// A SKU selling 1,000/week with 5 on hand is ALREADY out for decision purposes.
// Flags only — NO demand planning / replenishment math (explicit PRD-4 boundary).

/** On-hand below this share of weekly velocity = effectively out. */
export const EFFECTIVE_STOCKOUT_VELOCITY_SHARE = 0.15;

/**
 * true  → effectively quebrado (onHand < share × weekly velocity)
 * false → stocked relative to its own velocity
 * null  → velocity unknown or zero: a dead SKU is not a quiebre (never guess).
 */
export function effectivelyOut(onHand: number, weeklyVelocity: number | null): boolean | null {
  if (weeklyVelocity === null || weeklyVelocity <= 0) return null;
  return onHand < EFFECTIVE_STOCKOUT_VELOCITY_SHARE * weeklyVelocity;
}

export interface AvailabilityHonesty {
  totalSkus: number;
  /** % of SKUs the system reports as available (stock > 0). */
  reportedPct: number;
  /** % truly available: stock > 0 AND not effectively out. Always ≤ reported. */
  realPct: number;
  /** SKUs reported available but effectively quebrados. */
  flaggedSkus: number;
}

/**
 * Real vs reported availability (PRD-4 step 4.2 — Orlando's "96% reportado, 92%
 * real, y esos 4 puntos son venta a la vena"). null on empty input.
 */
export function availabilityHonesty(
  rows: Array<{ onHand: number; weeklyVelocity: number | null }>,
): AvailabilityHonesty | null {
  if (rows.length === 0) return null;
  const reported = rows.filter((r) => r.onHand > 0);
  const flagged = reported.filter((r) => effectivelyOut(r.onHand, r.weeklyVelocity) === true);
  const pct = (n: number) => Math.round((n / rows.length) * 10000) / 100;
  return {
    totalSkus: rows.length,
    reportedPct: pct(reported.length),
    realPct: pct(reported.length - flagged.length),
    flaggedSkus: flagged.length,
  };
}

/**
 * Weekly lost-sales ESTIMATE (PRD-4 step 4.3, D23): the weekly sales run-rate
 * of effectively-out SKUs. We cannot know how long each SKU has been quebrado,
 * so the honest figure is "≈$/semana en riesgo" — always label it estimación.
 * null when nothing is flagged (an estimate of nothing is not 0).
 */
export function lostSalesWeeklyEstimate(
  rows: Array<{ onHand: number; weeklyVelocity: number | null; weeklySalesValue: number }>,
): number | null {
  const flagged = rows.filter((r) => effectivelyOut(r.onHand, r.weeklyVelocity) === true);
  if (flagged.length === 0) return null;
  return flagged.reduce((s, r) => s + r.weeklySalesValue, 0);
}
